import type {
  AnchorHTMLAttributes,
  MouseEvent as ReactMouseEvent,
  ReactNode,
} from "react";
import { useRef } from "react";

import AppLink from "@components/link/link";
import { useLink } from "@components/link/link-helpers";
import type { toObjectProps } from "@components/link/link-helpers";

type Props = AnchorHTMLAttributes<HTMLAnchorElement> & {
  children?: ReactNode;
  to: string | toObjectProps;
};

const shouldHandleMouseDown = (event: ReactMouseEvent<HTMLAnchorElement>) => {
  if (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return false;
  }
  const target = event.currentTarget.getAttribute("target");
  return !target || target === "_self";
};

const AskAILink = ({ children, to, onClick, onMouseDown, ...props }: Props) => {
  const link = useLink();
  const navigatedOnMouseDownRef = useRef(false);

  const handleMouseDown = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    onMouseDown?.(event);
    if (event.defaultPrevented || !shouldHandleMouseDown(event)) {
      return;
    }
    navigatedOnMouseDownRef.current = true;
    event.preventDefault();
    // Streaming responses can re-render between mouse down and click.
    link.redirect(to, true);
  };

  const handleClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    if (navigatedOnMouseDownRef.current) {
      navigatedOnMouseDownRef.current = false;
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  return (
    <AppLink
      to={to}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      {...props}
    >
      {children}
    </AppLink>
  );
};

export default AskAILink;
